import { useMemo } from "react";
import intl from "react-intl-universal";
import { TbCoin, TbFilter, TbUsers, TbWorld } from "react-icons/tb";
import {
  BAAS_STABLES_WALLET_NETWORKS,
  BAAS_STABLES_WALLET_STATUSES,
} from "../../../../../Services/baasStablesWalletApi";
import { ModernFilterBar } from "../../../../../Components/Molecules/Filters/ModernFilterBar";
import { EMPTY_BAAS_STABLES_WALLET_LIST_FILTERS } from "../baasStablesWalletFilters";

const buildChipOptions = (values, labelPrefix, allLabel) => [
  { value: "all", label: allLabel },
  ...values.map((value) => ({
    value,
    label: intl.get(`${labelPrefix}.${value}`, { default: value }),
  })),
];

const ChipPanel = ({ options, field, draft, onChange }) => (
  <div className="extract-filter-panel__body">
    <div className="flex flex-wrap gap-2 p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
            draft[field] === option.value
              ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
              : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300"
          }`}
          onClick={() => onChange({ ...draft, [field]: option.value })}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

export const BaasStablesWalletsFilterBar = ({
  entities = [],
  assets = [],
  filters,
  onFiltersChange,
  filteredItems = [],
}) => {
  const assetOptions = useMemo(
    () => [
      { value: "all", label: intl.get("baas.stablesWallet.filters.allAssets") },
      ...assets.map((asset) => ({ value: asset, label: asset })),
    ],
    [assets],
  );

  const networkOptions = useMemo(
    () =>
      buildChipOptions(
        BAAS_STABLES_WALLET_NETWORKS,
        "baas.stablesWallet.network",
        intl.get("baas.stablesWallet.filters.allNetworks"),
      ),
    [],
  );

  const statusOptions = useMemo(
    () =>
      buildChipOptions(
        BAAS_STABLES_WALLET_STATUSES,
        "baas.stablesWallet.walletStatus",
        intl.get("baas.stablesWallet.filters.allStatuses"),
      ),
    [],
  );

  const entityOptions = useMemo(
    () => [
      { value: "all", label: intl.get("baas.stablesWallet.filters.allEntities") },
      ...entities.map((entity) => ({
        value: entity.entityId,
        label: entity.entityName,
      })),
    ],
    [entities],
  );

  const filterButtons = useMemo(
    () => [
      {
        id: "network",
        icon: TbWorld,
        label: intl.get("baas.stablesWallet.filters.network"),
        active: Boolean(filters.network && filters.network !== "all"),
      },
      {
        id: "status",
        icon: TbFilter,
        label: intl.get("baas.stablesWallet.filters.status"),
        active: Boolean(filters.status && filters.status !== "all"),
      },
      {
        id: "asset",
        icon: TbCoin,
        label: intl.get("baas.stablesWallet.filters.asset"),
        active: Boolean(filters.asset && filters.asset !== "all"),
      },
      {
        id: "entity",
        icon: TbUsers,
        label: intl.get("baas.stablesWallet.filters.entity"),
        active: Boolean(filters.entity && filters.entity !== "all"),
      },
    ],
    [filters.network, filters.status, filters.asset, filters.entity],
  );

  const renderPanelContent = (panelId, draft, onChange) => {
    if (panelId === "network") {
      return (
        <ChipPanel
          options={networkOptions}
          field="network"
          draft={draft}
          onChange={onChange}
        />
      );
    }

    if (panelId === "status") {
      return (
        <ChipPanel
          options={statusOptions}
          field="status"
          draft={draft}
          onChange={onChange}
        />
      );
    }

    if (panelId === "asset") {
      return (
        <ChipPanel options={assetOptions} field="asset" draft={draft} onChange={onChange} />
      );
    }

    if (panelId === "entity") {
      return (
        <ChipPanel options={entityOptions} field="entity" draft={draft} onChange={onChange} />
      );
    }

    return null;
  };

  return (
    <ModernFilterBar
      filters={filters}
      onFiltersChange={onFiltersChange}
      emptyFilters={EMPTY_BAAS_STABLES_WALLET_LIST_FILTERS}
      searchPlaceholder={intl.get("baas.stablesWallet.filters.walletSearchPlaceholder")}
      filterButtons={filterButtons}
      renderPanelContent={renderPanelContent}
      panelTitles={{
        network: intl.get("baas.stablesWallet.filters.network"),
        status: intl.get("baas.stablesWallet.filters.status"),
        asset: intl.get("baas.stablesWallet.filters.asset"),
        entity: intl.get("baas.stablesWallet.filters.entity"),
      }}
      filteredItems={filteredItems}
      showFilters
    />
  );
};
